import * as React from "react";

import { useLeadFormModuleContext } from "./LeadFormModuleProvider";
import { DefaultError } from "./Steps/DefaultError/DefaultError";
import { Ecommerce } from "./Steps/Ecommerce/Ecommerce";
import { Email } from "./Steps/Email/Email";
import { EmployeeCount } from "./Steps/EmployeeCount/EmployeeCount";
import {
  ExternalEventSuccess,
  ExternalEventUserInfo,
} from "./Steps/ExternalEventSteps";
import { FundingSources } from "./Steps/FundingSources/FundingSources";
import {
  GTMEmail,
  GTMNon3PStepOne,
  GTMNon3PStepTwo,
  GTMSuccess,
  GTMvbpimm,
} from "./Steps/GTMSteps";
import { GTMExistingLead } from "./Steps/GTMSteps/GTMExistingLead";
import { GTMInlineEmail } from "./Steps/GTMSteps/GTMInlineEmail";
import { GTMWebsite } from "./Steps/GTMSteps/GTMWebsite";
import { Industry } from "./Steps/Industry/Industry";
import { Loading, LongWaitLoading } from "./Steps/Loading/Loading";
import { LoadingStencil } from "./Steps/Loading/LoadingStencil";
import { MonthlySales } from "./Steps/MonthlySales/MonthlySales";
import { Password } from "./Steps/Password/Password";
import { Success } from "./Steps/Success/Success";
import { UserInfo } from "./Steps/UserInfo/UserInfo";
import { Waitlist } from "./Steps/Waitlist/Waitlist";
import { Website } from "./Steps/Website/Website";
import { LeadFormStateMachineState, LeadFormSteps } from "./machines/types";
import { FormType } from "./sharedTypes";

interface StepRendererProps {
  state: LeadFormStateMachineState;
  formType: FormType;
  isInlineEmail?: boolean;
  inlineEmailFieldWidth?: string;
  checkWorkEmail?: boolean;
}

export const LeadFormStepRenderer: React.FC<StepRendererProps> = ({
  state,
  formType,
}) => {
  /**
   * Two form types exist today.
   * GTM_LEAD_FORM - used via sanity. All Sanity instances of this form will automatically have this type
   * SALES_LEAD_FORM - anywhere else (custom code) will have this.
   * Check them out in ./sharedTypes
   */
  const isGTMForm = formType === FormType.GTM_LEAD_FORM;

  const { checkWorkEmail, isInlineEmail, inlineEmailFieldWidth } =
    useLeadFormModuleContext();

  const stateMatchesStep = React.useCallback(
    (step: LeadFormSteps) => {
      return (
        state.matches(step) ||
        Object.values(state.value).includes(step) ||
        Object.values(Object.values(state.value)[0]).includes(step)
      );
    },
    [state],
  );

  if (state.matches(LeadFormSteps.email)) {
    return <Email checkWorkEmail={checkWorkEmail} />;
  }

  if (stateMatchesStep(LeadFormSteps.userInfo)) {
    return <UserInfo />;
  }

  if (stateMatchesStep(LeadFormSteps.employeeCount)) {
    return <EmployeeCount />;
  }

  if (stateMatchesStep(LeadFormSteps.ecommerce)) {
    return <Ecommerce />;
  }

  if (stateMatchesStep(LeadFormSteps.monthlySales)) {
    return <MonthlySales />;
  }

  if (stateMatchesStep(LeadFormSteps.fundingSources)) {
    return <FundingSources />;
  }

  if (stateMatchesStep(LeadFormSteps.industry)) {
    return <Industry />;
  }
  if (stateMatchesStep(LeadFormSteps.password)) {
    return <Password />;
  }

  const creatingLead =
    stateMatchesStep(LeadFormSteps.createLead) ||
    stateMatchesStep(LeadFormSteps.createExistingLead);

  if (creatingLead && !isInlineEmail) {
    return isGTMForm ? <LoadingStencil /> : <Loading />;
  }

  if (stateMatchesStep(LeadFormSteps.updateLead)) {
    return isGTMForm && !isInlineEmail ? (
      <LoadingStencil />
    ) : (
      <LongWaitLoading />
    );
  }

  if (stateMatchesStep(LeadFormSteps.signup)) {
    return <Loading />;
  }

  if (stateMatchesStep(LeadFormSteps.success)) {
    return <Success />;
  }

  if (stateMatchesStep(LeadFormSteps.waitlist)) {
    return <Waitlist />;
  }

  if (stateMatchesStep(LeadFormSteps.website)) {
    return <Website />;
  }

  if (stateMatchesStep(LeadFormSteps.error)) {
    return (
      <DefaultError
        retryText="Refresh page"
        onClickRetry={() => {
          window.location.reload();
        }}
      />
    );
  }

  //GTM lead form components
  if (stateMatchesStep(LeadFormSteps.gtmEmail)) {
    return <GTMEmail checkWorkEmail={checkWorkEmail} />;
  }
  if (
    stateMatchesStep(LeadFormSteps.gtmInlineEmail) ||
    creatingLead ||
    (isInlineEmail && stateMatchesStep(LeadFormSteps.gtmSuccess))
  ) {
    return (
      <GTMInlineEmail
        isLoading={creatingLead || stateMatchesStep(LeadFormSteps.gtmSuccess)}
        success={stateMatchesStep(LeadFormSteps.gtmSuccess)}
        inlineEmailFieldWidth={inlineEmailFieldWidth}
        checkWorkEmail={checkWorkEmail}
      />
    );
  }
  if (stateMatchesStep(LeadFormSteps.gtmNon3PStepOne)) {
    return <GTMNon3PStepOne />;
  }
  if (stateMatchesStep(LeadFormSteps.gtmNon3PStepTwo)) {
    return <GTMNon3PStepTwo />;
  }
  if (stateMatchesStep(LeadFormSteps.gtmWebsite)) {
    return <GTMWebsite />;
  }
  if (stateMatchesStep(LeadFormSteps.gtmVBPIMM)) {
    return <GTMvbpimm />;
  }
  if (stateMatchesStep(LeadFormSteps.gtmExistingLead)) {
    return <GTMExistingLead />;
  }
  if (stateMatchesStep(LeadFormSteps.gtmSuccess)) {
    return <GTMSuccess />;
  }

  //External Event lead form components
  if (stateMatchesStep(LeadFormSteps.eventUserInfo)) {
    return <ExternalEventUserInfo />;
  }
  if (stateMatchesStep(LeadFormSteps.eventSuccess)) {
    return <ExternalEventSuccess />;
  }

  return <div>not matched</div>;
};
